import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import QueueSelect from "../QueueSelect";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  startupOptionsBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.02)"
        : "rgba(0,0,0,0.02)",
  },
  colorAdornment: {
    width: 18,
    height: 18,
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.18)",
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({
  open,
  onClose,
  whatsAppId,
  initialConnectionType = "baileys",
  allowedConnectionTypes = ["baileys", "waba", "webchat", "instagram", "messenger"],
}) => {
  const classes = useStyles();
  const { whatsApps, refreshWhatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const initialState = {
    name: "",
    connectionType:
      ["waba", "gupshup", "webchat", "instagram", "messenger"].includes(
        String(initialConnectionType || "baileys").toLowerCase()
      )
        ? String(initialConnectionType || "baileys").toLowerCase()
        : "baileys",
    greetingMessage: "",
    farewellMessage: "",
    defaultQueueId: "",
    allowGroupMessages: false,
    isDefault: false,
    importFullHistory: false,
    n8nWebhookEnabled: false,
    n8nWebhookUrl: "",
    n8nWebhookToken: "",
    n8nStopOnAccept: false,
    n8nStopOnQueue: false,
    typebotEnabled: false,
    typebotBaseUrl: "https://typebot.io",
    typebotPublicId: "",
    typebotApiToken: "",
    typebotStopOnAccept: false,
    typebotStopOnQueue: false,
    webchatTitle: "",
    webchatSubtitle: "Atendimento em tempo real",
    webchatCtaMessage: "Fale com a gente",
    webchatColor: "#008069",
    webchatSecondaryColor: "#00a884",
    webchatSurfaceColor: "#ffffff",
    webchatTextColor: "#0f172a",
    webchatWelcomeMessage: "",
    webchatPanelWidth: 420,
    webchatPanelHeight: 680,
    metaPageId: "",
    metaInstagramBusinessId: "",
    metaAccessToken: "",
    metaVerifyToken: "",
    metaWebhookUrl: "",
    gupshupAppName: "",
    gupshupAppId: "",
    gupshupApiKey: "",
    gupshupNumber: "",
    handoffEnabled: false,
    handoffWebchatId: "",
    handoffTtlHours: 24,
    webchatAppOnly: false,
    webchatAllowFileUpload: true,
    webchatAllowAudio: true,
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queueOptions, setQueueOptions] = useState([]);
  const [webchatColorPickerField, setWebchatColorPickerField] = useState("");

  useEffect(() => {
    if (open && !whatsAppId) {
      setWhatsApp({
        ...initialState,
        connectionType:
          ["waba", "gupshup", "webchat", "instagram", "messenger"].includes(
            String(initialConnectionType || "baileys").toLowerCase()
          )
            ? String(initialConnectionType || "baileys").toLowerCase()
            : "baileys",
      });
      setSelectedQueueIds([]);
    }
  }, [open, whatsAppId, initialConnectionType]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`, {
          params: { _: Date.now() },
        });
        setWhatsApp({
          ...initialState,
          ...data,
          connectionType: data?.connectionType || "baileys",
          // O numero da Gupshup e guardado em connectedNumber, que e o campo
          // que o resto do sistema usa. Sem esta linha o campo abria vazio na
          // edicao e parecia que o dado tinha se perdido.
          gupshupNumber: data?.connectedNumber || "",
          // null no Select do MUI vira aviso de componente nao controlado e
          // rotulo vazio; "" e o "nenhum" do Select.
          handoffWebchatId: data?.handoffWebchatId || "",
          handoffTtlHours: data?.handoffTtlHours || 24,
        });

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [open, whatsAppId]);

  useEffect(() => {
    const fetchQueues = async () => {
      if (!open) return;
      try {
        const { data } = await api.get("/queue");
        setQueueOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchQueues();
  }, [open]);

  const handleSaveWhatsApp = async (values) => {
    const rawConnectionType = String(values.connectionType || "baileys").toLowerCase();
    const normalizedConnectionType =
      rawConnectionType === "waba"
        ? "waba"
        : rawConnectionType === "gupshup"
        ? "gupshup"
        : rawConnectionType === "webchat"
        ? "webchat"
        : rawConnectionType === "instagram"
        ? "instagram"
        : rawConnectionType === "messenger"
        ? "messenger"
        : "baileys";

    const whatsappData = {
      name: values.name,
      isDefault: Boolean(values.isDefault),
      allowGroupMessages: Boolean(values.allowGroupMessages),
      importFullHistory: Boolean(values.importFullHistory),
      n8nWebhookEnabled: Boolean(values.n8nWebhookEnabled),
      n8nWebhookUrl: values.n8nWebhookUrl || "",
      n8nWebhookToken: values.n8nWebhookToken || "",
      n8nStopOnAccept: Boolean(values.n8nStopOnAccept),
      n8nStopOnQueue: Boolean(values.n8nStopOnQueue),
      typebotEnabled: Boolean(values.typebotEnabled),
      typebotBaseUrl: values.typebotBaseUrl || "",
      typebotPublicId: values.typebotPublicId || "",
      typebotApiToken: values.typebotApiToken || "",
      typebotStopOnAccept: Boolean(values.typebotStopOnAccept),
      typebotStopOnQueue: Boolean(values.typebotStopOnQueue),
      webchatTitle: values.webchatTitle || "",
      webchatSubtitle: values.webchatSubtitle || "",
      webchatCtaMessage: values.webchatCtaMessage || "",
      webchatColor: values.webchatColor || "",
      webchatSecondaryColor: values.webchatSecondaryColor || "",
      webchatSurfaceColor: values.webchatSurfaceColor || "",
      webchatTextColor: values.webchatTextColor || "",
      webchatWelcomeMessage: values.webchatWelcomeMessage || "",
      webchatPanelWidth: Number(values.webchatPanelWidth || 420),
      webchatPanelHeight: Number(values.webchatPanelHeight || 680),
      metaPageId: values.metaPageId || "",
      metaInstagramBusinessId: values.metaInstagramBusinessId || "",
      metaAccessToken: values.metaAccessToken || "",
      metaVerifyToken: values.metaVerifyToken || "",
      metaWebhookUrl: values.metaWebhookUrl || metaWebhookUrl,
      gupshupAppName: values.gupshupAppName || "",
      gupshupAppId: values.gupshupAppId || "",
      gupshupApiKey: values.gupshupApiKey || "",
      gupshupNumber: values.gupshupNumber || values.connectedNumber || "",
      handoffEnabled: Boolean(values.handoffEnabled),
      handoffWebchatId: values.handoffWebchatId || null,
      handoffTtlHours: Number(values.handoffTtlHours || 24),
      webchatAppOnly: Boolean(values.webchatAppOnly),
      webchatAllowFileUpload: values.webchatAllowFileUpload !== false,
      webchatAllowAudio: values.webchatAllowAudio !== false,
      greetingMessage: values.greetingMessage || "",
      farewellMessage: values.farewellMessage || "",
      defaultQueueId: values.defaultQueueId || null,
      queueIds: selectedQueueIds,
    };

    if (!whatsAppId) {
      whatsappData.connectionType = normalizedConnectionType;
    }

    try {
      if (whatsAppId) {
        const { data } = await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        setWhatsApp(data);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      await refreshWhatsApps();
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
  };

  const showBootstrapOptions = !whatsAppId && user?.profile === "admin";
  const currentConnectionType = String(
    whatsApp?.connectionType || initialConnectionType || "baileys"
  ).toLowerCase();
  const dialogTitle =
    currentConnectionType === "webchat"
      ? whatsAppId
        ? "Editar WebChat"
        : "Novo WebChat"
      : currentConnectionType === "instagram"
      ? whatsAppId
        ? "Editar Instagram Direct"
        : "Novo Instagram Direct"
      : currentConnectionType === "messenger"
      ? whatsAppId
        ? "Editar Facebook Messenger"
        : "Novo Facebook Messenger"
      : i18n.t(whatsAppId ? "whatsappModal.title.edit" : "whatsappModal.title.add");
  const metaWebhookUrl = `${String(api.defaults.baseURL || window.location.origin).replace(
    /\/+$/,
    ""
  )}/meta/webhook`;
  // A URL inteira e o segredo: o token no caminho e o que a Gupshup consegue
  // guardar, porque no painel dela so se cola o endereco - nao ha cabecalho nem
  // assinatura. O token so existe depois que a conexao foi criada.
  const gupshupWebhookUrl = whatsApp?.gupshupWebhookToken
    ? `${String(api.defaults.baseURL || window.location.origin).replace(
        /\/+$/,
        ""
      )}/gupshup/webhook/${whatsApp.gupshupWebhookToken}`
    : "";
  const generateMetaVerifyToken = () => {
    if (window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
        ""
      );
    }

    return `${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2)}${Math.random().toString(36).slice(2)}`;
  };
  const openWebchatColorPicker = (fieldName) => {
    setWebchatColorPickerField(fieldName);
  };
  const closeWebchatColorPicker = () => {
    setWebchatColorPickerField("");
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{whatsAppId ? `${dialogTitle} - ID #${whatsAppId}` : dialogTitle}</DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="isDefault"
                        checked={Boolean(values.isDefault)}
                        onChange={(event) =>
                          setFieldValue("isDefault", event.target.checked)
                        }
                      />
                    }
                    label={i18n.t("whatsappModal.form.default")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="allowGroupMessages"
                        checked={Boolean(values.allowGroupMessages)}
                        onChange={(event) =>
                          setFieldValue("allowGroupMessages", event.target.checked)
                        }
                      />
                    }
                    label="Atender grupos"
                  />
                </div>
                {!whatsAppId && (
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Tipo de conexão</InputLabel>
                    <Field
                      as={Select}
                      label="Tipo de conexão"
                      name="connectionType"
                    >
                      {allowedConnectionTypes.includes("baileys") && (
                        <MenuItem value="baileys">WhatsApp (QR Code)</MenuItem>
                      )}
                      {allowedConnectionTypes.includes("waba") && (
                        <MenuItem value="waba">
                          WABA (WhatsApp Business API)
                        </MenuItem>
                      )}
                      {/* Gupshup e uma intermediaria para a mesma conexao
                          oficial de WhatsApp, entao segue a contratacao da WABA
                          em vez de ter canal proprio no plano. */}
                      {allowedConnectionTypes.includes("waba") && (
                        <MenuItem value="gupshup">Gupshup (WhatsApp)</MenuItem>
                      )}
                      {allowedConnectionTypes.includes("webchat") && (
                        <MenuItem value="webchat">WebChat</MenuItem>
                      )}
                      {allowedConnectionTypes.includes("instagram") && (
                        <MenuItem value="instagram">Instagram Direct</MenuItem>
                      )}
                      {allowedConnectionTypes.includes("messenger") && (
                        <MenuItem value="messenger">Facebook Messenger</MenuItem>
                      )}
                    </Field>
                  </FormControl>
                )}
                {["instagram", "messenger"].includes(
                  String(values.connectionType || currentConnectionType).toLowerCase()
                ) && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">
                      {String(values.connectionType || currentConnectionType).toLowerCase() ===
                      "instagram"
                        ? "Instagram Direct"
                        : "Facebook Messenger"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Use o token de acesso da página/app Meta e configure este webhook no app.
                    </Typography>
                    {String(values.connectionType || currentConnectionType).toLowerCase() ===
                    "messenger" ? (
                      <Field
                        as={TextField}
                        label="Page ID"
                        fullWidth
                        name="metaPageId"
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <Field
                        as={TextField}
                        label="Instagram Business Account ID"
                        fullWidth
                        name="metaInstagramBusinessId"
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    <Field
                      as={TextField}
                      label="Access Token"
                      fullWidth
                      name="metaAccessToken"
                      type="password"
                      placeholder={whatsAppId ? "********" : ""}
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        autoComplete: "new-password",
                        spellCheck: "false",
                      }}
                    />
                    <TextField
                      label="Verify Token"
                      fullWidth
                      name="metaVerifyToken"
                      value={values.metaVerifyToken || ""}
                      onChange={(event) =>
                        setFieldValue("metaVerifyToken", event.target.value)
                      }
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() =>
                                setFieldValue(
                                  "metaVerifyToken",
                                  generateMetaVerifyToken()
                                )
                              }
                            >
                              Gerar
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="URL de callback"
                      fullWidth
                      value={values.metaWebhookUrl || metaWebhookUrl}
                      variant="outlined"
                      margin="dense"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
                {String(values.connectionType || currentConnectionType).toLowerCase() === "gupshup" && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">Gupshup</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Use as credenciais do app da Gupshup e cole a URL de callback no painel dela.
                    </Typography>
                    <Field
                      as={TextField}
                      label="Nome do app na Gupshup"
                      fullWidth
                      name="gupshupAppName"
                      variant="outlined"
                      margin="dense"
                      helperText="Igual ao cadastrado na Gupshup: e por ele que o webhook reconhece a conexao."
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="ID do app na Gupshup"
                      fullWidth
                      name="gupshupAppId"
                      variant="outlined"
                      margin="dense"
                      helperText="Aparece na aba Settings do app, e na URL do painel. Necessario para enviar template."
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="Numero da empresa"
                      fullWidth
                      name="gupshupNumber"
                      variant="outlined"
                      margin="dense"
                      helperText="So digitos, com pais e DDD. Ex.: 556899990000"
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="API key"
                      fullWidth
                      name="gupshupApiKey"
                      type="password"
                      placeholder={whatsAppId ? "********" : ""}
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        autoComplete: "new-password",
                        spellCheck: "false",
                      }}
                    />
                    <TextField
                      label="URL de callback"
                      fullWidth
                      value={
                        gupshupWebhookUrl ||
                        "Salve a conexao para gerar a URL de callback."
                      }
                      variant="outlined"
                      margin="dense"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Cole esta URL no painel da Gupshup, em Webhooks. Ela contem um segredo - trate como senha."
                    />
                  </Box>
                )}
                {["waba", "gupshup"].includes(
                  String(values.connectionType || currentConnectionType).toLowerCase()
                ) && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">Continuar no WebChat</Typography>
                    <Typography variant="caption" color="textSecondary">
                      O atendente envia ao cliente um link pessoal; a conversa segue no WebChat,
                      onde as mensagens não são cobradas pela Meta. Custa uma mensagem: a do link.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          name="handoffEnabled"
                          checked={Boolean(values.handoffEnabled)}
                          onChange={(event) =>
                            setFieldValue("handoffEnabled", event.target.checked)
                          }
                        />
                      }
                      label="Permitir enviar o atendimento para o WebChat"
                    />
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>WebChat que recebe a conversa</InputLabel>
                      <Field
                        as={Select}
                        label="WebChat que recebe a conversa"
                        name="handoffWebchatId"
                      >
                        <MenuItem value="">
                          <em>Nenhum</em>
                        </MenuItem>
                        {(whatsApps || [])
                          .filter(
                            (item) =>
                              String(item.connectionType || "").toLowerCase() === "webchat" &&
                              ((!item.archivedAt && !item.webchatAppOnly) ||
                                Number(item.id) === Number(values.handoffWebchatId))
                          )
                          .map((item) => (
                            // O alvo gravado continua na lista mesmo arquivado ou
                            // virado "so app": sem ele o campo aparecia em branco e
                            // nao dava para entender o erro.
                            <MenuItem key={item.id} value={item.id}>
                              {item.archivedAt
                                ? `${item.name} (arquivado)`
                                : item.webchatAppOnly
                                ? `${item.name} (só app)`
                                : item.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                    <Field
                      as={TextField}
                      label="Validade do link (horas)"
                      type="number"
                      name="handoffTtlHours"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 1, max: 72, step: 1 }}
                      helperText="Depois disso o link para de abrir. O cliente que já entrou continua conversando."
                    />
                  </Box>
                )}
                {String(values.connectionType || currentConnectionType).toLowerCase() === "webchat" && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">Widget WebChat</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Configure a aparencia basica do widget que sera instalado no site.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          name="webchatAppOnly"
                          checked={Boolean(values.webchatAppOnly)}
                          onChange={(event) =>
                            setFieldValue("webchatAppOnly", event.target.checked)
                          }
                        />
                      }
                      label="Só conversas abertas pelo app"
                    />
                    <Typography variant="caption" color="textSecondary" display="block">
                      Para o chat dentro do app do assinante. Ligada, esta conexão só aceita
                      conversa aberta com o código que o servidor do provedor pede para o
                      assinante logado, e o script de instalação em sites deixa de funcionar
                      nela. Use uma conexão só para o app, com fila padrão.
                    </Typography>
                    <Box mt={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              name="webchatAllowFileUpload"
                              checked={values.webchatAllowFileUpload !== false}
                              onChange={(event) =>
                                setFieldValue("webchatAllowFileUpload", event.target.checked)
                              }
                            />
                          }
                          label="Cliente pode enviar arquivos (foto, vídeo e documento)"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              name="webchatAllowAudio"
                              checked={values.webchatAllowAudio !== false}
                              onChange={(event) =>
                                setFieldValue("webchatAllowAudio", event.target.checked)
                              }
                            />
                          }
                          label="Cliente pode enviar áudio"
                        />
                        <Typography variant="caption" color="textSecondary" display="block">
                          Valem para o chat do app e para o link de continuação do WhatsApp. No
                          chat do site o visitante não envia arquivos. Desligada, o botão some e o
                          envio é recusado. No app, o botão só aparece se o app também tiver o
                          envio de arquivos ou a gravação de áudio. O atendente sempre pode enviar
                          mídia ao cliente.
                        </Typography>
                    </Box>
                    <Field
                      as={TextField}
                      label="Título do widget"
                      fullWidth
                      name="webchatTitle"
                      placeholder="Atendimento"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="Subtitulo do widget"
                      fullWidth
                      name="webchatSubtitle"
                      placeholder="Atendimento em tempo real"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="Mensagem de chamada (CTA)"
                      fullWidth
                      name="webchatCtaMessage"
                      placeholder="Fale com a gente"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                    <Field
                      as={TextField}
                      label="Cor principal"
                      fullWidth
                      name="webchatColor"
                      placeholder="#008069"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              className={classes.colorAdornment}
                              style={{ backgroundColor: values.webchatColor || "#008069" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => openWebchatColorPicker("webchatColor")}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      label="Cor secundaria"
                      fullWidth
                      name="webchatSecondaryColor"
                      placeholder="#00a884"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              className={classes.colorAdornment}
                              style={{
                                backgroundColor: values.webchatSecondaryColor || "#00a884",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => openWebchatColorPicker("webchatSecondaryColor")}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      label="Cor de fundo (surface)"
                      fullWidth
                      name="webchatSurfaceColor"
                      placeholder="#ffffff"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              className={classes.colorAdornment}
                              style={{
                                backgroundColor: values.webchatSurfaceColor || "#ffffff",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => openWebchatColorPicker("webchatSurfaceColor")}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      label="Cor de texto"
                      fullWidth
                      name="webchatTextColor"
                      placeholder="#0f172a"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              className={classes.colorAdornment}
                              style={{ backgroundColor: values.webchatTextColor || "#0f172a" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => openWebchatColorPicker("webchatTextColor")}
                          >
                            <Colorize />
                          </IconButton>
                        ),
                      }}
                    />
                    <ColorPicker
                      open={Boolean(webchatColorPickerField)}
                      currentColor={values[webchatColorPickerField] || "#0f766e"}
                      handleClose={closeWebchatColorPicker}
                      onChange={(color) => {
                        if (webchatColorPickerField) {
                          setFieldValue(webchatColorPickerField, color);
                        }
                        closeWebchatColorPicker();
                      }}
                    />
                    <Field
                      as={TextField}
                      label="Mensagem inicial"
                      fullWidth
                      multiline
                      rows={3}
                      name="webchatWelcomeMessage"
                      placeholder="Ola! Como podemos ajudar?"
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                    <Box className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Largura do painel (px)"
                        fullWidth
                        type="number"
                        name="webchatPanelWidth"
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 420, max: 760, step: 10 }}
                      />
                      <Field
                        as={TextField}
                        label="Altura do painel (px)"
                        fullWidth
                        type="number"
                        name="webchatPanelHeight"
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 460, max: 900, step: 10 }}
                      />
                    </Box>
                    {values.webchatToken ? (
                      <TextField
                        label="Script de instalação"
                        fullWidth
                        multiline
                        rows={4}
                        value={`<script src="${String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "")}/webchat/fab.js?token=${values.webchatToken}" data-webchat-channel-id="${values.webchatToken}" data-api-url="${String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "")}" async><\/script>`}
                        variant="outlined"
                        margin="dense"
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : null}
                  </Box>
                )}
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Fila padrão da conexão</InputLabel>
                  <Field
                    as={Select}
                    label="Fila padrão da conexão"
                    name="defaultQueueId"
                  >
                    <MenuItem value="">
                      <em>Sem fila padrão</em>
                    </MenuItem>
                    {queueOptions
                      .filter((queue) => selectedQueueIds.includes(queue.id))
                      .map((queue) => (
                        <MenuItem key={queue.id} value={queue.id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                  </Field>
                </FormControl>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    rows={5}
                    fullWidth
                    name="greetingMessage"
                    error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                    helperText={
                      (touched.greetingMessage && errors.greetingMessage) ||
                      "Variáveis: {{name}}, {{firstname}}, {{ticketId}} (número do ticket) e {{protocol}} (data + número)"
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.farewellMessage")}
                    type="farewellMessage"
                    multiline
                    rows={5}
                    fullWidth
                    name="farewellMessage"
                    error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                    helperText={
                      (touched.farewellMessage && errors.farewellMessage) ||
                      "Variáveis: {{name}}, {{firstname}}, {{ticketId}} (número do ticket) e {{protocol}} (data + número)"
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
                />
                <Box className={classes.startupOptionsBox}>
                  <Typography variant="subtitle2">
                    Integração n8n
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="n8nWebhookEnabled"
                        checked={Boolean(values.n8nWebhookEnabled)}
                        onChange={(event) =>
                          setFieldValue("n8nWebhookEnabled", event.target.checked)
                        }
                      />
                    }
                    label="Ativar webhook ao receber mensagem"
                  />
                  <Field
                    as={TextField}
                    label="Endpoint webhook"
                    fullWidth
                    name="n8nWebhookUrl"
                    placeholder="https://seu-n8n/webhook/deskadvisor"
                    variant="outlined"
                    margin="dense"
                  />
                  <Field
                    as={TextField}
                    label="Token (Bearer)"
                    fullWidth
                    name="n8nWebhookToken"
                    variant="outlined"
                    margin="dense"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="n8nStopOnAccept"
                        checked={Boolean(values.n8nStopOnAccept)}
                        onChange={(event) =>
                          setFieldValue("n8nStopOnAccept", event.target.checked)
                        }
                      />
                    }
                    label="Interromper n8n quando o chamado for aceito"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="n8nStopOnQueue"
                        checked={Boolean(values.n8nStopOnQueue)}
                        onChange={(event) =>
                          setFieldValue("n8nStopOnQueue", event.target.checked)
                        }
                      />
                    }
                    label="Interromper n8n quando o chamado sair da fila padrão"
                  />
                </Box>
                <Box className={classes.startupOptionsBox}>
                  <Typography variant="subtitle2">
                    Integração Typebot
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Use o public ID do Typebot e um token de API. A conversa do bot fica vinculada ao ticket.
                  </Typography>
                  <Box mt={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          name="typebotEnabled"
                          checked={Boolean(values.typebotEnabled)}
                          onChange={(event) =>
                            setFieldValue("typebotEnabled", event.target.checked)
                          }
                        />
                      }
                      label="Ativar Typebot ao receber mensagem"
                    />
                  </Box>
                  <Field
                    as={TextField}
                    label="URL do Typebot"
                    fullWidth
                    name="typebotBaseUrl"
                    placeholder="https://typebot.io"
                    variant="outlined"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Field
                    as={TextField}
                    label="Public ID do Typebot"
                    fullWidth
                    name="typebotPublicId"
                    placeholder="meu-typebot-public-id"
                    variant="outlined"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Field
                    as={TextField}
                    label="Token da API Typebot (opcional)"
                    fullWidth
                    name="typebotApiToken"
                    type="password"
                    variant="outlined"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="typebotStopOnAccept"
                        checked={Boolean(values.typebotStopOnAccept)}
                        onChange={(event) =>
                          setFieldValue("typebotStopOnAccept", event.target.checked)
                        }
                      />
                    }
                    label="Interromper Typebot quando o chamado for aceito"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        name="typebotStopOnQueue"
                        checked={Boolean(values.typebotStopOnQueue)}
                        onChange={(event) =>
                          setFieldValue("typebotStopOnQueue", event.target.checked)
                        }
                      />
                    }
                    label="Interromper Typebot quando o chamado sair da fila padrão"
                  />
                </Box>
                {showBootstrapOptions && (
                  <Box className={classes.startupOptionsBox}>
                    <Typography variant="subtitle2">
                      Configurações da primeira sincronização
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          name="importFullHistory"
                          checked={Boolean(values.importFullHistory)}
                          onChange={(event) =>
                            setFieldValue("importFullHistory", event.target.checked)
                          }
                        />
                      }
                      label="Puxar histórico dos últimos 10 dias"
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
